/**
 *
 * <SearchForm />
 *
 */
import React, { useState } from 'react';
import connect from 'react-redux/lib/connect/connect';
import PropTypes from 'prop-types';
import reduxForm from 'redux-form/lib/reduxForm';
import formValueSelector from 'redux-form/lib/formValueSelector';
import Field from 'redux-form/lib/Field';
import getFormSyncErrors from 'redux-form/lib/getFormSyncErrors';
import { ControlLabel, FormGroup, Panel, Form } from 'react-bootstrap';
import FaInfoCircle from 'react-icons/lib/fa/info-circle';
import { Popup } from 'semantic-ui-react';
import { FormattedMessage } from 'react-intl';
import { isEmpty, toNumber } from 'lodash';
import MultiSelect from 'components/MultiSelect';
import 'react-datepicker/dist/react-datepicker.css';
import classNames from 'classnames';
import DatePicker from 'components/DatePicker';
import OptionFormattedMessage from 'components/OptionFormattedMessage';
import { TRANSFER_DIRECTIONS, TRANSFER_REVIEW_ROLES, TRANSFER_REVIEW_ROLES_PARTIAL } from 'services/search';
import { messages as commonMessages, TRANSFER_TYPES } from 'common';
import { TransferType } from 'common/constants';
import componentMessages from './messages';
import styles from './styles.css';
import { TRANSFER_STATE_MUTLISELECT_OPTIONS } from './constants';
import { limitLength } from '../../utils';
import { MinMaxInputs } from './MinMaxInputs';

const messages = { ...commonMessages, ...componentMessages };

export const makeDatePicker = (field) => DatePicker(field);

export const validate = (formValues) => {
  const { max_amount, min_amount } = formValues;
  if (!isEmpty(max_amount) && toNumber(min_amount) > toNumber(max_amount)) {
    return { min_amount: 'Min is > Max' };
  }
  return {};
};

export const SearchForm = ({
  reset: resetFromReduxForm,
  handleSubmit,
  isSearchLoading,
  onTransferStateInformationIconClicked,
  transferTypeValue,
  showAllFields,
  formSyncErrors,
  resetFormInitialValues,
}) => {
  const [transferStateInputFocused, setStateInputFocused] = useState(false);
  const handleClear = (e) => {
    e.preventDefault();
    resetFormInitialValues();
    resetFromReduxForm();
  };

  return (
    <Panel className={styles.searchPanel}>
      <Form className={styles.searchForm} horizontal onSubmit={handleSubmit}>
        <FormGroup>
          <div className={styles.formColumn}>
            <ControlLabel htmlFor="transfer_id">
              <FormattedMessage {...messages.transferId} />
            </ControlLabel>
            <Field
              name="transfer_id"
              type="text"
              component="input"
              className={classNames('form-control', styles.field)}
            />
          </div>
          <div className={styles.formColumn}>
            <ControlLabel htmlFor="apex_account">
              <FormattedMessage {...messages.clearingAccount} />
            </ControlLabel>
            <Popup
              size="large"
              position="top left"
              content="For transfers involving two accounts, this field searches both."
              trigger={
                <FaInfoCircle data-test-handle="accountInfoIcon" className={styles.transferStateInformationIcon} />
              }
            />
            <Field
              name="apex_account"
              type="text"
              component="input"
              className={classNames('form-control', styles.field)}
            />
          </div>
          <div className={styles.formColumn}>
            <ControlLabel htmlFor="start_date">
              <FormattedMessage {...messages.startDate} />
            </ControlLabel>
            <Field
              name="start_date"
              type="date"
              className={classNames('form-control', styles.field)}
              component={makeDatePicker}
            />
          </div>
          <div className={styles.formColumn}>
            <ControlLabel htmlFor="end_date">
              <FormattedMessage {...messages.endDate} />
            </ControlLabel>
            <Field
              name="end_date"
              type="date"
              className={classNames('form-control', styles.field)}
              component={makeDatePicker}
            />
          </div>
          <div className={classNames(styles.wideFormColumn, styles.multiSelect)}>
            <ControlLabel htmlFor="state">
              <FormattedMessage {...messages.state} />
            </ControlLabel>
            <FaInfoCircle
              className={styles.transferStateInformationIcon}
              onClick={onTransferStateInformationIconClicked}
            />
            <Field
              name="state"
              component={MultiSelect}
              onFocus={() => setStateInputFocused(true)}
              onBlur={() => setStateInputFocused(false)}
              focused={transferStateInputFocused}
              options={TRANSFER_STATE_MUTLISELECT_OPTIONS}
              classNamePrefix="state-select"
              isMulti
              maxMenuHeight={200}
            />
          </div>
          <div className={styles.formColumn}>
            <ControlLabel htmlFor="view_option">
              <FormattedMessage {...messages.reviewRole} />
            </ControlLabel>
            <Field name="view_option" component="select" className={classNames('form-control', styles.field)}>
              <option />
              {Object.keys(showAllFields ? TRANSFER_REVIEW_ROLES : TRANSFER_REVIEW_ROLES_PARTIAL).map((key) => (
                <OptionFormattedMessage message={messages[key]} key={key} value={TRANSFER_REVIEW_ROLES[key]} />
              ))}
            </Field>
          </div>
          <div className={styles.formColumn}>
            <ControlLabel htmlFor="direction">
              <FormattedMessage {...messages.direction} />
            </ControlLabel>
            <Field name="direction" component="select" className={classNames('form-control', styles.field)}>
              <option />
              {Object.keys(TRANSFER_DIRECTIONS).map((key) => (
                <OptionFormattedMessage message={messages[key]} key={key} value={TRANSFER_DIRECTIONS[key]} />
              ))}
            </Field>
          </div>
          {showAllFields && (
            <div className={styles.formColumn}>
              <ControlLabel htmlFor="correspondent">
                <FormattedMessage {...messages.correspondentCode} />
              </ControlLabel>
              <Field
                name="correspondent"
                type="text"
                component="input"
                normalize={(value) => limitLength(value, 4)}
                className={classNames('form-control', styles.field)}
              />
            </div>
          )}
          <div className={styles.formColumn}>
            <ControlLabel htmlFor="transfer_type">
              <FormattedMessage {...messages.transferType} />
            </ControlLabel>
            <Field name="transfer_type" component="select" className={classNames('form-control', styles.field)}>
              <option />
              {Object.values(TransferType).map((transferType, index) => (
                <option value={Object.keys(TransferType)[index]} key={Object.keys(TransferType)[index]}>
                  {transferType}
                </option>
              ))}
            </Field>
          </div>
          {showAllFields && (
            <React.Fragment>
              <div className={styles.formColumn}>
                <ControlLabel htmlFor="journal_program_name">
                  <FormattedMessage {...messages.programName} />
                </ControlLabel>
                <Field
                  name="journal_program_name"
                  disabled={transferTypeValue !== TRANSFER_TYPES.journal}
                  type="text"
                  component="input"
                  className={classNames('form-control', styles.field)}
                />
              </div>
            </React.Fragment>
          )}
          <div className={styles.formColumn}>
            <ControlLabel htmlFor="fed_wire_reference_number">
              <FormattedMessage {...messages.fedRefNum} />
            </ControlLabel>
            <Field
              name="fed_wire_reference_number"
              type="text"
              component="input"
              className={classNames('form-control', styles.field)}
            />
          </div>
          {showAllFields && (
            <React.Fragment>
              <div className={styles.formColumn}>
                <ControlLabel htmlFor="house_bank_account_number">
                  <FormattedMessage {...messages.houseBankAccountNumber} />
                </ControlLabel>
                <Field
                  name="house_bank_account_number"
                  type="text"
                  component="input"
                  className={classNames('form-control', styles.field)}
                />
              </div>
            </React.Fragment>
          )}
          <MinMaxInputs formSyncErrors={formSyncErrors} />
          <div div className={styles.button}>
            <button
              disabled={isSearchLoading || !isEmpty(formSyncErrors)}
              className={`buttons ${
                isSearchLoading || !isEmpty(formSyncErrors) ? 'buttons-disabled' : 'buttons-primary'
              }`}
              type="submit"
              data-test-handle="submitSearch"
            >
              <FormattedMessage {...messages.submit} />
            </button>
            <button className="button-clear" onClick={handleClear} data-test-handle="clearBtn">
              Clear
            </button>
          </div>
        </FormGroup>
      </Form>
    </Panel>
  );
};

SearchForm.propTypes = {
  reset: PropTypes.func.isRequired,
  clearFields: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  isSearchLoading: PropTypes.bool.isRequired,
  onTransferStateInformationIconClicked: PropTypes.func.isRequired,
  transferTypeValue: PropTypes.string,
  showAllFields: PropTypes.bool.isRequired,
  formSyncErrors: PropTypes.object.isRequired,
  resetFormInitialValues: PropTypes.func.isRequired,
};

SearchForm.defaultProps = {
  transferTypeValue: undefined,
};

export { TRANSFER_STATE_MUTLISELECT_OPTIONS };
const ConnectedForm = reduxForm({ form: 'Search', validate, enableReinitialize: true })(SearchForm);

const selector = formValueSelector('Search');

export default connect((state) => ({
  transferTypeValue: selector(state, 'transfer_type'),
  formSyncErrors: getFormSyncErrors('Search')(state),
}))(ConnectedForm);



// WEBPACK FOOTER //
// ./src/components/SearchForm/index.js